import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    Heading,
    Input,
    Select,
    SimpleGrid,
    Spinner,
    Text,
    Textarea,
    useColorModeValue,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import axiosInstance from '../../../api/axiosInstance';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';

const standardFooter = {
    legal_link_text: 'DETALII Retur, Termeni și Condiții, Politica de confidențialitate',
    legal_link_url: '/termeni-si-conditii',
    image_urls: [
        'https://produsul-perfect.ro/wp-content/uploads/2024/03/1-1.png',
        'https://produsul-perfect.ro/wp-content/uploads/2024/03/2-1.png',
    ],
};

const fallbackContent = {
    theme: {
        primary_color: '#B74EB7',
    },
    hero: {
        social_proof_image_url: '',
        social_proof_text: '',
        headline: '',
        offer: '',
        cta_text: '',
        price_hint: '',
        description: '',
        benefits: ['', '', ''],
    },
    feature_cards: [
        { image_url: '', body: '' },
        { image_url: '', body: '' },
        { image_url: '', body: '' },
    ],
    pricing: {
        current_price: '',
        old_price: '',
        currency: 'lei',
        benefits: ['', '', '', ''],
        cta_text: '',
    },
    form: {
        container_id: 'form-container',
    },
    reviews: {
        section_title: '',
        rating_value: 4.8,
        items: [
            { image_url: '', title: '', quote: '', reviewer_name: '' },
            { image_url: '', title: '', quote: '', reviewer_name: '' },
            { image_url: '', title: '', quote: '', reviewer_name: '' },
        ],
    },
    faq: {
        title: 'Întrebări frecvente',
        items: [
            { question: 'În cât timp ajunge produsul?', answer: 'Comenzile ajung de obicei în termen de 24-48 de ore lucrătoare.' },
            { question: 'Mă va contacta curierul?', answer: 'În ziua livrării, veți fi contactat de curier pentru a fi notificat de livrarea comenzii.' },
            { question: 'Cum efectuez plata?', answer: 'Comanda o veți plăti curierului la primirea pachetului. Plata poate fi făcută numerar.' },
            { question: 'Ce fac dacă vreau să returnez produsul?', answer: 'Dacă doriți să returnați produsul achiziționat, ne puteți contacta pe email și vă vom ghida mai departe pentru a finaliza procesul de returnare. Acest lucru poate fi făcut în maxim 14 zile de la primirea comenzii.' },
            { question: 'Cât costă produsul și livrarea?', answer: 'Toate informațiile despre preț pot fi vizualizate în formularul de comandă.' },
            { question: 'Cum vă pot contacta?', answer: 'Ne puteți contacta prin e-mail pentru orice întrebare, reclamație sau sugestie.' },
        ],
    },
    footer: {
        ...standardFooter,
    },
};

const EXAMPLE_CONTENT = {
    ro: {
        hero: {
            headline: 'Descoperă produsul care îți simplifică fiecare zi',
            offer: 'Reducere 50% doar astăzi',
            cta_text: 'Comandă acum',
            price_hint: 'Vezi prețul în formular',
            description: 'Un produs creat pentru a-ți face viața mai ușoară, testat și apreciat de mii de clienți mulțumiți.',
            social_proof_text: 'Peste 10.000 de clienți mulțumiți din toată România',
            benefits: ['Livrare rapidă în 24-48h', 'Plată ramburs la curier', 'Retur gratuit în 14 zile'],
        },
        feature_cards: [
            { body: 'Ușor de folosit, chiar din prima zi.' },
            { body: 'Materiale rezistente, gândite să dureze.' },
            { body: 'Compact și practic, îl poți lua oriunde.' },
        ],
        pricing: {
            current_price: '149',
            old_price: '299',
            currency: 'lei',
            cta_text: 'Comandă acum cu reducere',
            benefits: ['Livrare gratuită', 'Plată ramburs', 'Garanție 12 luni', 'Retur gratuit 14 zile'],
        },
        reviews: {
            section_title: 'Ce spun clienții noștri',
            rating_value: 4.8,
            items: [
                { title: 'Foarte mulțumită!', quote: 'Produsul a ajuns rapid și este exact cum l-am văzut în poze. Recomand!', reviewer_name: 'Andreea M.' },
                { title: 'Calitate excelentă', quote: 'M-a surprins plăcut calitatea materialelor. Merită banii.', reviewer_name: 'Cristian P.' },
                { title: 'Livrare rapidă', quote: 'A ajuns în 2 zile, curierul a fost punctual. Voi mai comanda.', reviewer_name: 'Elena R.' },
            ],
        },
    },
    en: {
        hero: {
            headline: 'Discover the product that simplifies your everyday routine',
            offer: '50% off today only',
            cta_text: 'Order now',
            price_hint: 'See the price in the form',
            description: 'A product built to make your life easier, tested and loved by thousands of happy customers.',
            social_proof_text: 'Over 10,000 happy customers nationwide',
            benefits: ['Fast 24-48h delivery', 'Cash on delivery', 'Free returns within 14 days'],
        },
        feature_cards: [
            { body: 'Easy to use from day one.' },
            { body: 'Durable materials built to last.' },
            { body: 'Compact and practical, take it anywhere.' },
        ],
        pricing: {
            current_price: '39',
            old_price: '79',
            currency: 'USD',
            cta_text: 'Order now at this price',
            benefits: ['Free shipping', 'Cash on delivery', '12-month warranty', 'Free 14-day returns'],
        },
        reviews: {
            section_title: 'What our customers say',
            rating_value: 4.8,
            items: [
                { title: 'Very happy!', quote: 'The product arrived quickly and looks exactly like the photos. Highly recommend!', reviewer_name: 'Sarah M.' },
                { title: 'Excellent quality', quote: 'Pleasantly surprised by the build quality. Worth every penny.', reviewer_name: 'James P.' },
                { title: 'Fast delivery', quote: 'Arrived in 2 days, courier was on time. Will order again.', reviewer_name: 'Emma R.' },
            ],
        },
    },
};

const mergeContent = (base, incoming) => ({
    ...base,
    ...(incoming || {}),
    theme: { ...base.theme, ...(incoming?.theme || {}) },
    hero: { ...base.hero, ...(incoming?.hero || {}) },
    feature_cards: incoming?.feature_cards || base.feature_cards,
    pricing: { ...base.pricing, ...(incoming?.pricing || {}) },
    form: { ...base.form, ...(incoming?.form || {}) },
    reviews: {
        ...base.reviews,
        ...(incoming?.reviews || {}),
        items: incoming?.reviews?.items || base.reviews.items,
    },
    faq: {
        ...base.faq,
        ...(incoming?.faq || {}),
        items: incoming?.faq?.items || base.faq.items,
    },
    footer: {
        ...standardFooter,
        legal_link_url: incoming?.footer?.legal_link_url || base.footer?.legal_link_url || standardFooter.legal_link_url,
    },
});

const EditorSection = ({ eyebrow, title, description, action, children }) => {
    const cardBg = useColorModeValue('white', 'navy.800');
    const cardBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
    const titleColor = useColorModeValue('gray.800', 'white');

    return (
        <Box mb="6" p={{ base: 4, md: 6 }} borderWidth="1px" borderColor={cardBorderColor} borderRadius="2xl" bg={cardBg} boxShadow="sm">
            <Flex mb="5" justify="space-between" align={{ base: 'flex-start', sm: 'center' }} gap="3" direction={{ base: 'column', sm: 'row' }}>
                <Box>
                    {eyebrow && (
                        <Badge colorScheme="blue" borderRadius="full" mb="2" px="3" py="1">
                            {eyebrow}
                        </Badge>
                    )}
                    <Heading size="md" color={titleColor}>
                        {title}
                    </Heading>
                    {description && (
                        <Text color="gray.500" fontSize="sm" mt="1">
                            {description}
                        </Text>
                    )}
                </Box>
                {action}
            </Flex>
            {children}
        </Box>
    );
};

const TextField = ({ label, value, onChange, placeholder, type = 'text', onFocus, fieldId }) => (
    <FormControl>
        <FormLabel>{label}</FormLabel>
        <Input type={type} value={value || ''} placeholder={placeholder} data-landing-field={fieldId} onFocus={onFocus} onChange={(event) => onChange(event.target.value)} />
    </FormControl>
);

const TextAreaField = ({ label, value, onChange, placeholder, rows = 3, onFocus, fieldId }) => (
    <FormControl>
        <FormLabel>{label}</FormLabel>
        <Textarea value={value || ''} placeholder={placeholder} rows={rows} data-landing-field={fieldId} onFocus={onFocus} onChange={(event) => onChange(event.target.value)} />
    </FormControl>
);

const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const ColorField = ({ label, value, onChange, onFocus, fieldId }) => {
    const swatchValue = HEX_COLOR_PATTERN.test(value || '') ? value : '#B74EB7';

    return (
        <FormControl>
            <FormLabel>{label}</FormLabel>
            <Flex gap="2" align="center">
                <Input
                    type="color"
                    value={swatchValue}
                    onFocus={onFocus}
                    onChange={(event) => onChange(event.target.value)}
                    width="48px"
                    minW="48px"
                    px="1"
                    py="1"
                    cursor="pointer"
                />
                <Input
                    type="text"
                    value={value || ''}
                    placeholder="#B74EB7"
                    data-landing-field={fieldId}
                    onFocus={onFocus}
                    onChange={(event) => onChange(event.target.value)}
                />
            </Flex>
        </FormControl>
    );
};

const MediaUploadField = ({ label, value, onUpload, isUploading, onFocus, hint, isRo, fieldId }) => (
    <FormControl>
        <FormLabel>{label}</FormLabel>
        <Input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            disabled={isUploading}
            data-landing-field={fieldId}
            onFocus={onFocus}
            onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                    onUpload(file);
                }
                event.target.value = '';
            }}
        />
        {hint && (
            <Text fontSize="xs" color="gray.400" mt="2">
                {hint}
            </Text>
        )}
        <Text fontSize="xs" color={value ? 'gray.500' : 'gray.400'} mt="1">
            {isUploading
                ? (isRo ? 'Se incarca media...' : 'Uploading media...')
                : value
                    ? (isRo ? 'Media selectata' : 'Media selected')
                    : (isRo ? 'Nu exista media incarcata' : 'No media uploaded yet')}
        </Text>
    </FormControl>
);

const PreviewImage = ({ src, alt, isRo }) => {
    const [hasError, setHasError] = useState(false);
    const imageUrl = typeof src === 'string' ? src.trim() : '';

    useEffect(() => {
        setHasError(false);
    }, [imageUrl]);

    return imageUrl && !hasError ? (
        <Box
            as="img"
            src={imageUrl}
            alt={alt}
            width="100%"
            height="auto"
            borderRadius="lg"
            bg="gray.100"
            onError={() => setHasError(true)}
        />
    ) : (
        <Flex minH="120px" align="center" justify="center" borderRadius="lg" bg="gray.100" color="gray.400" fontSize="sm">
            {isRo ? 'Previzualizare imagine/GIF' : 'Preview image/GIF'}
        </Flex>
    );
};

const PreviewScrollContext = React.createContext(null);

const PreviewHighlight = ({ activeField, field, children, scrollOnActive = true, isRo, ...props }) => {
    const ref = useRef(null);
    const previewContext = React.useContext(PreviewScrollContext);
    const scrollContainerRef = previewContext?.scrollContainerRef;
    const onFieldSelect = previewContext?.onFieldSelect;
    const fields = Array.isArray(field) ? field : [field];
    const isActive = fields.includes(activeField);
    const shouldScroll = isActive && scrollOnActive;

    const handleClick = (event) => {
        if (!onFieldSelect) {
            return;
        }

        event.stopPropagation();
        onFieldSelect(fields[0]);
    };

    useEffect(() => {
        const scrollContainer = scrollContainerRef?.current;

        if (shouldScroll && ref.current && scrollContainer) {
            const scrollToActiveElement = () => {
                if (!ref.current) {
                    return;
                }

                const containerRect = scrollContainer.getBoundingClientRect();
                const elementRect = ref.current.getBoundingClientRect();
                const nextScrollTop = scrollContainer.scrollTop
                    + elementRect.top
                    - containerRect.top
                    - (scrollContainer.clientHeight / 2)
                    + (elementRect.height / 2);

                scrollContainer.scrollTo({
                    top: Math.max(0, nextScrollTop),
                    behavior: 'smooth',
                });
            };

            requestAnimationFrame(scrollToActiveElement);
        }
    }, [shouldScroll, activeField, scrollContainerRef]);

    return (
        <Box
            ref={ref}
            position="relative"
            scrollMargin="24px"
            borderWidth={isActive ? '2px' : '0'}
            borderColor={isActive ? 'blue.400' : 'transparent'}
            boxShadow={isActive ? '0 0 0 4px rgba(66, 153, 225, 0.18)' : undefined}
            transition="all 0.15s ease"
            cursor={onFieldSelect ? 'pointer' : undefined}
            title={onFieldSelect ? (isRo ? 'Click pentru a edita acest camp' : 'Click to edit this field') : undefined}
            onClick={handleClick}
            {...props}
        >
            {isActive && (
                <Badge position="absolute" top="2" right="2" colorScheme="blue" zIndex="1">
                    {isRo ? 'Editezi aici' : 'Editing here'}
                </Badge>
            )}
            {children}
        </Box>
    );
};

const LiveLandingPreview = ({ content, supportsReviews, activeField, isRo, onFieldSelect }) => {
    const l = (en, ro) => (isRo ? ro : en);
    const primaryColor = content.theme.primary_color || '#B74EB7';
    const scrollContainerRef = useRef(null);
    const previewContextValue = useMemo(
        () => ({ scrollContainerRef, onFieldSelect }),
        [scrollContainerRef, onFieldSelect]
    );
    const cardBg = useColorModeValue('white', 'navy.800');
    const cardBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
    const headerBorderColor = useColorModeValue('gray.100', 'whiteAlpha.200');

    return (
        <Box borderWidth="1px" borderColor={cardBorderColor} borderRadius="2xl" bg={cardBg} boxShadow="sm" overflow="hidden" h={{ base: 'auto', xl: '100%' }} display="flex" flexDirection="column">
            <Box p="4" borderBottomWidth="1px" borderColor={headerBorderColor}>
                <Badge colorScheme="purple" borderRadius="full" mb="2" px="3" py="1">
                    {l('Live Preview', 'Previzualizare live')}
                </Badge>
                <Heading size="sm">{l('Landing page structure', 'Structura paginii de landing')}</Heading>
                <Text color="gray.500" fontSize="xs" mt="1">
                    {l(
                        'This preview updates instantly and shows where each field appears. Final rendering still happens in Elementor after import.',
                        'Aceasta previzualizare se actualizeaza instant si arata unde apare fiecare camp. Randarea finala are loc in Elementor dupa import.'
                    )}
                </Text>
            </Box>

            <Box ref={scrollContainerRef} p="4" flex={{ xl: '1' }} minH={{ xl: 0 }} overflowY={{ base: 'visible', xl: 'auto' }} bg="gray.50">
                <PreviewScrollContext.Provider value={previewContextValue}>
                <PreviewHighlight activeField={activeField} field="hero.image" bg="white" borderRadius="xl" p="4" mb="4" boxShadow="sm" isRo={isRo}>
                    <PreviewImage src={content.hero.social_proof_image_url} alt={l('Hero preview', 'Previzualizare hero')} isRo={isRo} />
                    <Text fontSize="xs" fontWeight="700" textAlign="center" mt="3" color="gray.600">
                        ★★★★★
                    </Text>
                    <PreviewHighlight activeField={activeField} field="hero.socialProof" borderRadius="md" p="1" mt="2" isRo={isRo}>
                    <Text fontSize="sm" textAlign="center" color="gray.600">
                        {content.hero.social_proof_text || l('Social proof text', 'Text social proof')}
                    </Text>
                    </PreviewHighlight>
                </PreviewHighlight>

                <Box bg="white" borderRadius="xl" p="4" mb="4" textAlign="center" boxShadow="sm">
                    <PreviewHighlight activeField={activeField} field="hero.headline" borderRadius="md" p="1" isRo={isRo}>
                    <Heading size="sm">{content.hero.headline || l('Hero headline', 'Titlu hero')}</Heading>
                    </PreviewHighlight>
                    <PreviewHighlight activeField={activeField} field={['hero.offer', 'theme.color']} scrollOnActive={activeField !== 'theme.color'} borderRadius="md" p="1" mt="2" isRo={isRo}>
                    <Text mt="3" fontSize="2xl" fontWeight="800" color={primaryColor}>
                        {content.hero.offer || l('Offer', 'Oferta')}
                    </Text>
                    </PreviewHighlight>
                    <PreviewHighlight activeField={activeField} field={['hero.cta', 'theme.color']} scrollOnActive={activeField !== 'theme.color'} borderRadius="md" p="1" mt="2" isRo={isRo}>
                    <Button mt="3" size="sm" color="white" bg={primaryColor} _hover={{ bg: primaryColor }}>
                        {content.hero.cta_text || l('CTA text', 'Text CTA')}
                    </Button>
                    </PreviewHighlight>
                    <PreviewHighlight activeField={activeField} field={['hero.priceHint', 'theme.color']} scrollOnActive={activeField !== 'theme.color'} borderRadius="md" p="1" mt="2" isRo={isRo}>
                    <Text mt="2" fontSize="xs" color={primaryColor} textDecoration="underline">
                        {content.hero.price_hint || l('Price hint', 'Hint pret')}
                    </Text>
                    </PreviewHighlight>
                    <PreviewHighlight activeField={activeField} field="hero.description" borderRadius="md" p="1" mt="2" isRo={isRo}>
                    <Text mt="3" fontSize="sm" fontWeight="600">
                        {content.hero.description || l('Product description', 'Descriere produs')}
                    </Text>
                    </PreviewHighlight>
                    <Box mt="3">
                        {(content.hero.benefits || []).map((benefit, index) => (
                            <PreviewHighlight key={index} activeField={activeField} field={`hero.benefit.${index}`} borderRadius="md" p="1" isRo={isRo}>
                            <Text fontSize="sm" fontWeight="600">
                                {benefit || `${l('Benefit', 'Beneficiu')} ${index + 1}`}
                            </Text>
                            </PreviewHighlight>
                        ))}
                    </Box>
                </Box>

                <SimpleGrid columns={{ base: 1, md: 3, xl: 1 }} spacing="3" mb="4">
                    {(content.feature_cards || []).map((card, index) => (
                        <PreviewHighlight key={index} activeField={activeField} field={`feature.${index}.image`} bg="white" borderRadius="xl" p="3" boxShadow="sm" isRo={isRo}>
                            <PreviewImage src={card.image_url} alt={`${l('Feature', 'Caracteristica')} ${index + 1}`} isRo={isRo} />
                            <PreviewHighlight activeField={activeField} field={`feature.${index}.body`} borderRadius="md" p="1" mt="2" isRo={isRo}>
                            <Text mt="3" fontSize="sm" color="gray.600" whiteSpace="pre-line">
                                {card.body || `${l('Feature card', 'Card caracteristica')} ${index + 1}`}
                            </Text>
                            </PreviewHighlight>
                            {index === 1 && (
                                <Button mt="3" size="xs" width="100%" color="white" bg={primaryColor} _hover={{ bg: primaryColor }}>
                                    {content.hero.cta_text || l('CTA text', 'Text CTA')}
                                </Button>
                            )}
                        </PreviewHighlight>
                    ))}
                </SimpleGrid>

                <Box bg="white" borderRadius="xl" p="4" mb="4" textAlign="center" boxShadow="sm">
                    <PreviewHighlight activeField={activeField} field={['pricing.current', 'theme.color']} scrollOnActive={activeField !== 'theme.color'} borderRadius="md" p="1" isRo={isRo}>
                    <Text fontSize="2xl" fontWeight="800" color={primaryColor}>
                        {content.pricing.current_price || 'XX.XX'} {content.pricing.currency || 'lei'}
                    </Text>
                    </PreviewHighlight>
                    <PreviewHighlight activeField={activeField} field="pricing.old" borderRadius="md" p="1" mt="1" isRo={isRo}>
                    <Text color="gray.400" textDecoration="line-through">
                        {content.pricing.old_price || 'YY.YY'} {content.pricing.currency || 'lei'}
                    </Text>
                    </PreviewHighlight>
                    <Box mt="3" textAlign="left">
                        {(content.pricing.benefits || []).map((benefit, index) => (
                            <PreviewHighlight key={index} activeField={activeField} field={`pricing.benefit.${index}`} borderRadius="md" p="1" isRo={isRo}>
                            <Text fontSize="sm" py="1" borderBottomWidth={index < content.pricing.benefits.length - 1 ? '1px' : '0'}>
                                {benefit || `${l('Pricing benefit', 'Beneficiu pret')} ${index + 1}`}
                            </Text>
                            </PreviewHighlight>
                        ))}
                    </Box>
                    <PreviewHighlight activeField={activeField} field={['pricing.cta', 'theme.color']} scrollOnActive={activeField !== 'theme.color'} borderRadius="md" p="1" mt="2" isRo={isRo}>
                    <Button mt="4" size="sm" width="100%" color="white" bg={primaryColor} _hover={{ bg: primaryColor }}>
                        {content.pricing.cta_text || l('Pricing CTA', 'CTA pret')}
                    </Button>
                    </PreviewHighlight>
                </Box>

                <PreviewHighlight activeField={activeField} field="form.container" bg="white" borderRadius="xl" p="4" mb="4" textAlign="center" borderWidth="1px" borderStyle="dashed" borderColor="gray.300" isRo={isRo}>
                    <Text fontWeight="700">{l('Order form embed', 'Embed formular comanda')}</Text>
                    <Text fontSize="xs" color="gray.500">
                        {l('Container', 'Container')}: {content.form.container_id || 'form-container'}
                    </Text>
                </PreviewHighlight>

                {supportsReviews && (
                    <Box bg="white" borderRadius="xl" p="4" mb="4" boxShadow="sm">
                        <PreviewHighlight activeField={activeField} field="reviews.title" borderRadius="md" p="1" mb="2" isRo={isRo}>
                        <Heading size="sm" textAlign="center" mb="3">
                            {content.reviews.section_title || l('Reviews title', 'Titlu review-uri')}
                        </Heading>
                        </PreviewHighlight>
                        {(content.reviews.items || []).map((review, index) => (
                            <PreviewHighlight key={index} activeField={activeField} field={[`review.${index}.image`, `review.${index}.title`, `review.${index}.quote`, `review.${index}.name`]} py="3" borderTopWidth={index === 0 ? '0' : '1px'} borderColor="gray.100" isRo={isRo}>
                                <PreviewImage src={review.image_url} alt={`${l('Review', 'Review')} ${index + 1}`} isRo={isRo} />
                                <Text mt="2" fontWeight="700">{review.title || `${l('Review', 'Review')} ${index + 1}`}</Text>
                                <Text fontSize="xs" color="gray.500">★★★★★</Text>
                                <Text fontSize="sm" color="gray.600">"{review.quote || l('Review quote', 'Text review')}"</Text>
                                <Text fontSize="xs" fontWeight="700" mt="1">{review.reviewer_name || l('Reviewer name', 'Nume reviewer')}</Text>
                            </PreviewHighlight>
                        ))}
                    </Box>
                )}

                <Box bg="white" borderRadius="xl" p="4" mb="4" boxShadow="sm">
                    <PreviewHighlight activeField={activeField} field="faq.title" borderRadius="md" p="1" mb="2" isRo={isRo}>
                    <Heading size="sm" textAlign="center" mb="3">
                        {content.faq.title || l('FAQ title', 'Titlu FAQ')}
                    </Heading>
                    </PreviewHighlight>
                    {(content.faq.items || []).map((item, index) => (
                        <PreviewHighlight key={index} activeField={activeField} field={[`faq.${index}.question`, `faq.${index}.answer`]} py="2" borderTopWidth={index === 0 ? '0' : '1px'} borderColor="gray.100" isRo={isRo}>
                            <Text fontSize="sm" fontWeight="700">{item.question || `${l('FAQ question', 'Intrebare FAQ')} ${index + 1}`}</Text>
                            <Text fontSize="xs" color="gray.500">{item.answer || l('FAQ answer', 'Raspuns FAQ')}</Text>
                        </PreviewHighlight>
                    ))}
                </Box>

                <Box bg="white" borderRadius="xl" p="4" textAlign="center" boxShadow="sm">
                    <PreviewHighlight activeField={activeField} field={['footer.legalText', 'theme.color']} scrollOnActive={activeField !== 'theme.color'} borderRadius="md" p="1" isRo={isRo}>
                    <Box
                        display="inline-flex"
                        maxW="100%"
                        px="4"
                        py="2"
                        borderRadius="full"
                        color="white"
                        bg={primaryColor}
                        fontSize="sm"
                        fontWeight="700"
                        whiteSpace="normal"
                        wordBreak="break-word"
                        overflowWrap="anywhere"
                        lineHeight="1.3"
                    >
                        {content.footer.legal_link_text || l('Legal link', 'Link legal')}
                    </Box>
                    <Text fontSize="xs" color="gray.400" mt="2" wordBreak="break-all">
                        {content.footer.legal_link_url || l('Terms & conditions link', 'Link termeni si conditii')}
                    </Text>
                    </PreviewHighlight>
                    <SimpleGrid columns="2" spacing="3" mt="4">
                        {(content.footer.image_urls || []).map((imageUrl, index) => (
                            <PreviewHighlight key={index} activeField={activeField} field={`footer.image.${index}`} borderRadius="lg" isRo={isRo}>
                            <PreviewImage key={index} src={imageUrl} alt={`${l('Footer', 'Footer')} ${index + 1}`} isRo={isRo} />
                            </PreviewHighlight>
                        ))}
                    </SimpleGrid>
                </Box>
                </PreviewScrollContext.Provider>
            </Box>
        </Box>
    );
};

const LandingPageBuilder = () => {
    const { t, i18n } = useTranslation();
    const tr = (key, defaultValue) => t(key, { defaultValue });
    const isRo = i18n.language === 'ro';
    const l = (en, ro) => (isRo ? ro : en);
    const { productId } = useParams();
    const { addNotification, addErrorNotifications } = useNotification();
    const [content, setContent] = useState(fallbackContent);
    const [templateKey, setTemplateKey] = useState('full-purple');
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [uploadingSlots, setUploadingSlots] = useState({});
    const [activeField, setActiveField] = useState('');
    const [previewTop, setPreviewTop] = useState(160);
    const [isDirty, setIsDirty] = useState(false);
    const editorGridRef = useRef(null);
    const infoCardBg = useColorModeValue('gray.50', 'whiteAlpha.100');
    const infoCardBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
    const infoCardTitleColor = useColorModeValue('gray.700', 'gray.200');

    const selectedTemplate = useMemo(
        () => templates.find((template) => template.value === templateKey),
        [templateKey, templates]
    );
    const supportsReviews = selectedTemplate?.supports_reviews !== false;
    const focusPreview = (field) => () => setActiveField(field);

    const focusEditorField = (field) => {
        const targetField = Array.isArray(field) ? field[0] : field;
        if (!targetField) {
            return;
        }

        setActiveField(targetField);

        const input = document.querySelector(`[data-landing-field="${targetField}"]`);
        if (!input) {
            return;
        }

        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input.focus({ preventScroll: true });
    };

    useEffect(() => {
        let isMounted = true;

        const fetchLandingPage = async () => {
            setIsLoading(true);
            try {
                const response = await axiosInstance.get(`/products/${productId}/landing-page`);
                if (!isMounted) {
                    return;
                }

                const data = response.data || {};
                const defaultContent = mergeContent(fallbackContent, data.default_content);
                setTemplates(data.templates || []);
                setTemplateKey(data.template_key || 'full-purple');
                setContent(mergeContent(defaultContent, data.content));
                setIsDirty(false);
            } catch (error) {
                addErrorNotifications(error.response?.data || error);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchLandingPage();

        return () => {
            isMounted = false;
        };
    }, [addErrorNotifications, productId]);

    useEffect(() => {
        if (isLoading) {
            return undefined;
        }

        const animationFrames = [];
        let resizeObserver = null;

        const updatePreviewTop = () => {
            const gridRect = editorGridRef.current?.getBoundingClientRect();

            if (!gridRect) {
                return;
            }

            setPreviewTop(Math.max(24, Math.round(gridRect.top)));
        };

        const schedulePreviewTopUpdate = () => {
            animationFrames.push(window.requestAnimationFrame(updatePreviewTop));
        };

        updatePreviewTop();
        schedulePreviewTopUpdate();
        animationFrames.push(window.requestAnimationFrame(schedulePreviewTopUpdate));

        if (window.ResizeObserver && editorGridRef.current) {
            resizeObserver = new window.ResizeObserver(schedulePreviewTopUpdate);
            resizeObserver.observe(editorGridRef.current);
        }

        window.addEventListener('resize', schedulePreviewTopUpdate);

        return () => {
            animationFrames.forEach((animationFrame) => window.cancelAnimationFrame(animationFrame));
            resizeObserver?.disconnect();

            window.removeEventListener('resize', schedulePreviewTopUpdate);
        };
    }, [isLoading]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (!isDirty) {
                return;
            }

            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isDirty]);

    const updateContent = (section, key, value) => {
        setIsDirty(true);
        setContent((current) => ({
            ...current,
            [section]: {
                ...current[section],
                [key]: value,
            },
        }));
    };

    const updateArrayValue = (section, key, index, value) => {
        setIsDirty(true);
        setContent((current) => {
            const values = [...(current[section][key] || [])];
            values[index] = value;

            return {
                ...current,
                [section]: {
                    ...current[section],
                    [key]: values,
                },
            };
        });
    };

    const updateObjectArrayValue = (section, key, index, field, value) => {
        setIsDirty(true);
        setContent((current) => {
            const values = [...(current[section][key] || [])];
            values[index] = {
                ...(values[index] || {}),
                [field]: value,
            };

            return {
                ...current,
                [section]: {
                    ...current[section],
                    [key]: values,
                },
            };
        });
    };

    const updateTopLevelObjectArrayValue = (key, index, field, value) => {
        setIsDirty(true);
        setContent((current) => {
            const values = [...(current[key] || [])];
            values[index] = {
                ...(values[index] || {}),
                [field]: value,
            };

            return {
                ...current,
                [key]: values,
            };
        });
    };

    const handleTemplateChange = (value) => {
        const nextTemplate = templates.find((template) => template.value === value);
        setIsDirty(true);
        setTemplateKey(value);

        if (nextTemplate?.primary_color) {
            updateContent('theme', 'primary_color', nextTemplate.primary_color);
        }
    };

    const handleSave = async (contentToSave = content) => {
        setIsSaving(true);
        try {
            await axiosInstance.put(`/products/${productId}/landing-page`, {
                template_key: templateKey,
                content: contentToSave,
                status: 'draft',
            });
            addNotification(tr('landing.messages.saved', 'Landing page saved successfully.'), 'success');
            setIsDirty(false);
            return true;
        } catch (error) {
            addErrorNotifications(error.response?.data || error);
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const contentSnapshot = content;
            const didSave = await handleSave(contentSnapshot);
            if (!didSave) {
                return;
            }

            const response = await axiosInstance.get(`/products/${productId}/landing-page/export`, {
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `product-${productId}-elementor-template.json`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            addNotification(tr('landing.messages.exported', 'Elementor JSON exported successfully.'), 'success');
        } catch (error) {
            addErrorNotifications(error.response?.data || error);
        } finally {
            setIsExporting(false);
        }
    };

    const uploadMedia = async (slot, file, applyUrl) => {
        setUploadingSlots((current) => ({ ...current, [slot]: true }));

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('slot', slot);

            const response = await axiosInstance.post(`/products/${productId}/landing-page/media`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            applyUrl(response.data.url);
            addNotification(tr('landing.messages.mediaUploaded', 'Media uploaded successfully.'), 'success');
        } catch (error) {
            addErrorNotifications(error.response?.data || error);
        } finally {
            setUploadingSlots((current) => ({ ...current, [slot]: false }));
        }
    };

    const fillExampleContent = (section) => {
        const example = EXAMPLE_CONTENT[isRo ? 'ro' : 'en'][section];
        if (!example) {
            return;
        }

        setIsDirty(true);
        setContent((current) => {
            if (section === 'feature_cards') {
                return {
                    ...current,
                    feature_cards: current.feature_cards.map((card, index) => ({
                        ...card,
                        body: example[index]?.body ?? card.body,
                    })),
                };
            }

            if (section === 'reviews') {
                return {
                    ...current,
                    reviews: {
                        ...current.reviews,
                        section_title: example.section_title,
                        rating_value: example.rating_value,
                        items: current.reviews.items.map((item, index) => ({
                            ...item,
                            title: example.items[index]?.title ?? item.title,
                            quote: example.items[index]?.quote ?? item.quote,
                            reviewer_name: example.items[index]?.reviewer_name ?? item.reviewer_name,
                        })),
                    },
                };
            }

            return {
                ...current,
                [section]: {
                    ...current[section],
                    ...example,
                },
            };
        });
    };

    if (isLoading) {
        return (
            <Flex minH="60vh" align="center" justify="center">
                <Spinner size="xl" />
            </Flex>
        );
    }

    return (
        <Box maxWidth="xxl" px={{ base: '20px', md: '40px' }} py={{ base: '130px', md: '80px' }}>
            <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap="4" mb="6" direction={{ base: 'column', md: 'row' }}>
                <Box>
                    <Text color="gray.500" mt="1">
                        {tr('landing.pageDescription', 'Edit product content and export Elementor JSON template for WordPress.')}
                    </Text>
                </Box>
                <Flex gap="3" wrap="wrap">
                    <Button onClick={() => handleSave()} isLoading={isSaving} variant="outline" colorScheme="blue">
                        {tr('landing.save', 'Save')}
                    </Button>
                    <Button onClick={handleExport} isLoading={isExporting} colorScheme="blue" leftIcon={<DownloadIcon />}>
                        {tr('landing.saveExport', 'Save & Export JSON')}
                    </Button>
                </Flex>
            </Flex>

            <Grid ref={editorGridRef} templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 420px' }} gap="6" alignItems="start">
                <Box>
            <EditorSection
                eyebrow={l('Template', 'Template')}
                title={l('Template and Theme', 'Template si tema')}
                description={l('Choose the Elementor structure and primary color for this product.', 'Alege structura Elementor si culoarea principala pentru acest produs.')}
            >
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                    <FormControl>
                        <FormLabel>{l('Template', 'Template')}</FormLabel>
                        <Select value={templateKey} onFocus={focusPreview('template')} onChange={(event) => handleTemplateChange(event.target.value)}>
                            {templates.map((template) => (
                                <option key={template.value} value={template.value}>
                                    {template.label}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                    <ColorField
                        label={l('Primary color', 'Culoare principala')}
                        value={content.theme.primary_color}
                        fieldId="theme.color"
                        onFocus={focusPreview('theme.color')}
                        onChange={(value) => updateContent('theme', 'primary_color', value)}
                    />
                </SimpleGrid>
            </EditorSection>

            <EditorSection
                eyebrow={l('Hero', 'Hero')}
                title={l('Hero Section', 'Sectiune Hero')}
                description={l('Main offer, call to action, and first social-proof visual.', 'Oferta principala, call to action si primul vizual de social proof.')}
                action={
                    <Button size="sm" variant="ghost" colorScheme="blue" onClick={() => fillExampleContent('hero')}>
                        {l('Fill with example content', 'Completeaza cu continut exemplu')}
                    </Button>
                }
            >
                <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap="4">
                    <TextField label={l('Headline', 'Titlu')} value={content.hero.headline} fieldId="hero.headline" onFocus={focusPreview('hero.headline')} onChange={(value) => updateContent('hero', 'headline', value)} />
                    <TextField label={l('Offer', 'Oferta')} value={content.hero.offer} fieldId="hero.offer" onFocus={focusPreview('hero.offer')} onChange={(value) => updateContent('hero', 'offer', value)} />
                    <TextField label={l('CTA text', 'Text CTA')} value={content.hero.cta_text} fieldId="hero.cta" onFocus={focusPreview('hero.cta')} onChange={(value) => updateContent('hero', 'cta_text', value)} />
                    <TextField label={l('Price hint', 'Hint pret')} value={content.hero.price_hint} fieldId="hero.priceHint" onFocus={focusPreview('hero.priceHint')} onChange={(value) => updateContent('hero', 'price_hint', value)} />
                    <MediaUploadField
                        label={l('Hero image/GIF', 'Imagine/GIF hero')}
                        value={content.hero.social_proof_image_url}
                        isUploading={!!uploadingSlots.hero}
                        fieldId="hero.image"
                        onFocus={focusPreview('hero.image')}
                        onUpload={(file) => uploadMedia('hero', file, (url) => updateContent('hero', 'social_proof_image_url', url))}
                        hint={l('Recommended: 800x800px or larger, square works best. Not cropped. Max 10MB.', 'Recomandat: 800x800px sau mai mare, format patrat. Fara crop. Maxim 10MB.')}
                        isRo={isRo}
                    />
                    <TextField label={l('Form container ID', 'ID container formular')} value={content.form.container_id} fieldId="form.container" onFocus={focusPreview('form.container')} onChange={(value) => updateContent('form', 'container_id', value)} />
                </Grid>
                <Box mt="4">
                    <TextAreaField label={l('Description', 'Descriere')} value={content.hero.description} fieldId="hero.description" onFocus={focusPreview('hero.description')} onChange={(value) => updateContent('hero', 'description', value)} />
                </Box>
                <Box mt="4">
                    <TextAreaField label={l('Social proof text', 'Text social proof')} value={content.hero.social_proof_text} fieldId="hero.socialProof" onFocus={focusPreview('hero.socialProof')} onChange={(value) => updateContent('hero', 'social_proof_text', value)} />
                </Box>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing="4" mt="4">
                    {content.hero.benefits.map((benefit, index) => (
                        <TextField
                            key={index}
                            label={`${l('Benefit', 'Beneficiu')} ${index + 1}`}
                            value={benefit}
                            fieldId={`hero.benefit.${index}`}
                            onFocus={focusPreview(`hero.benefit.${index}`)}
                            onChange={(value) => updateArrayValue('hero', 'benefits', index, value)}
                        />
                    ))}
                </SimpleGrid>
            </EditorSection>

            <EditorSection
                eyebrow={l('Features', 'Caracteristici')}
                title={l('Feature Cards', 'Carduri de caracteristici')}
                description={l('Three visual cards used in the middle of the Elementor landing page.', 'Trei carduri vizuale folosite in zona centrala a paginii Elementor.')}
                action={
                    <Button size="sm" variant="ghost" colorScheme="blue" onClick={() => fillExampleContent('feature_cards')}>
                        {l('Fill with example content', 'Completeaza cu continut exemplu')}
                    </Button>
                }
            >
                {content.feature_cards.map((card, index) => (
                    <Box key={index} mb="5">
                        <Heading size="sm" mb="3">
                            {l('Feature card', 'Card caracteristica')} {index + 1}
                        </Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                            <MediaUploadField
                                label={l('Image/GIF', 'Imagine/GIF')}
                                value={card.image_url}
                                isUploading={!!uploadingSlots[`feature-${index}`]}
                                fieldId={`feature.${index}.image`}
                                onFocus={focusPreview(`feature.${index}.image`)}
                                onUpload={(file) => uploadMedia(`feature-${index}`, file, (url) => updateTopLevelObjectArrayValue('feature_cards', index, 'image_url', url))}
                                hint={l('Recommended: at least 700x700px, square works best. Not cropped. Max 10MB.', 'Recomandat: minim 700x700px, format patrat. Fara crop. Maxim 10MB.')}
                                isRo={isRo}
                            />
                            <TextAreaField
                                label={l('Body', 'Continut')}
                                value={card.body}
                                rows={4}
                                fieldId={`feature.${index}.body`}
                                onFocus={focusPreview(`feature.${index}.body`)}
                                onChange={(value) => updateTopLevelObjectArrayValue('feature_cards', index, 'body', value)}
                            />
                        </SimpleGrid>
                        {index < content.feature_cards.length - 1 && <Divider mt="5" />}
                    </Box>
                ))}
            </EditorSection>

            <EditorSection
                eyebrow={l('Pricing', 'Pret')}
                title={l('Pricing Card', 'Card de pret')}
                description={l('The price box and conversion benefits near the form.', 'Zona de pret si beneficii de conversie langa formular.')}
                action={
                    <Button size="sm" variant="ghost" colorScheme="blue" onClick={() => fillExampleContent('pricing')}>
                        {l('Fill with example content', 'Completeaza cu continut exemplu')}
                    </Button>
                }
            >
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing="4">
                    <TextField label={l('Current price', 'Pret curent')} value={content.pricing.current_price} fieldId="pricing.current" onFocus={focusPreview('pricing.current')} onChange={(value) => updateContent('pricing', 'current_price', value)} />
                    <TextField label={l('Old price', 'Pret vechi')} value={content.pricing.old_price} fieldId="pricing.old" onFocus={focusPreview('pricing.old')} onChange={(value) => updateContent('pricing', 'old_price', value)} />
                    <TextField label={l('Currency', 'Moneda')} value={content.pricing.currency} onFocus={focusPreview('pricing.current')} onChange={(value) => updateContent('pricing', 'currency', value)} />
                    <TextField label={l('CTA text', 'Text CTA')} value={content.pricing.cta_text} fieldId="pricing.cta" onFocus={focusPreview('pricing.cta')} onChange={(value) => updateContent('pricing', 'cta_text', value)} />
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4" mt="4">
                    {content.pricing.benefits.map((benefit, index) => (
                        <TextField
                            key={index}
                            label={`${l('Pricing benefit', 'Beneficiu pret')} ${index + 1}`}
                            value={benefit}
                            fieldId={`pricing.benefit.${index}`}
                            onFocus={focusPreview(`pricing.benefit.${index}`)}
                            onChange={(value) => updateArrayValue('pricing', 'benefits', index, value)}
                        />
                    ))}
                </SimpleGrid>
            </EditorSection>

            {supportsReviews && (
                <EditorSection
                    eyebrow={l('Reviews', 'Review-uri')}
                    title={l('Customer Reviews', 'Review-uri clienti')}
                    description={l('Only the full Elementor template renders this section.', 'Aceasta sectiune apare doar in template-ul Elementor complet.')}
                    action={
                        <Button size="sm" variant="ghost" colorScheme="blue" onClick={() => fillExampleContent('reviews')}>
                            {l('Fill with example content', 'Completeaza cu continut exemplu')}
                        </Button>
                    }
                >
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4" mb="5">
                        <TextField label={l('Section title', 'Titlu sectiune')} value={content.reviews.section_title} fieldId="reviews.title" onFocus={focusPreview('reviews.title')} onChange={(value) => updateContent('reviews', 'section_title', value)} />
                        <TextField label={l('Rating value', 'Valoare rating')} type="number" value={content.reviews.rating_value} onFocus={focusPreview('reviews.title')} onChange={(value) => updateContent('reviews', 'rating_value', value)} />
                    </SimpleGrid>
                    {content.reviews.items.map((review, index) => (
                        <Box key={index} mb="5">
                            <Heading size="sm" mb="3">
                                {l('Review', 'Review')} {index + 1}
                            </Heading>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                                <MediaUploadField
                                    label={l('Image', 'Imagine')}
                                    value={review.image_url}
                                    isUploading={!!uploadingSlots[`review-${index}`]}
                                    fieldId={`review.${index}.image`}
                                    onFocus={focusPreview(`review.${index}.image`)}
                                    onUpload={(file) => uploadMedia(`review-${index}`, file, (url) => updateObjectArrayValue('reviews', 'items', index, 'image_url', url))}
                                    hint={l('Recommended: 600x400px (3:2 ratio). Image is cropped to fit. Max 10MB.', 'Recomandat: 600x400px (raport 3:2). Imaginea este cropata la afisare. Maxim 10MB.')}
                                    isRo={isRo}
                                />
                                <TextField label={l('Reviewer name', 'Nume reviewer')} value={review.reviewer_name} fieldId={`review.${index}.name`} onFocus={focusPreview(`review.${index}.name`)} onChange={(value) => updateObjectArrayValue('reviews', 'items', index, 'reviewer_name', value)} />
                                <TextField label={l('Title', 'Titlu')} value={review.title} fieldId={`review.${index}.title`} onFocus={focusPreview(`review.${index}.title`)} onChange={(value) => updateObjectArrayValue('reviews', 'items', index, 'title', value)} />
                                <TextAreaField label={l('Quote', 'Text review')} value={review.quote} fieldId={`review.${index}.quote`} onFocus={focusPreview(`review.${index}.quote`)} onChange={(value) => updateObjectArrayValue('reviews', 'items', index, 'quote', value)} />
                            </SimpleGrid>
                            {index < content.reviews.items.length - 1 && <Divider mt="5" />}
                        </Box>
                    ))}
                </EditorSection>
            )}

            <EditorSection
                eyebrow="FAQ"
                title={l('FAQ Accordion', 'Acordeon FAQ')}
                description={l(
                    'Six questions and answers rendered in the Elementor FAQ block. New landing pages reuse the FAQ from your last saved landing page automatically.',
                    'Sase intrebari si raspunsuri randate in blocul FAQ Elementor. Paginile noi preiau automat FAQ-ul din ultima pagina de landing salvata.'
                )}
            >
                <TextField label={l('FAQ title', 'Titlu FAQ')} value={content.faq.title} fieldId="faq.title" onFocus={focusPreview('faq.title')} onChange={(value) => updateContent('faq', 'title', value)} />
                {content.faq.items.map((item, index) => (
                    <Box key={index} mt="5">
                        <Heading size="sm" mb="3">
                            {l('FAQ item', 'Element FAQ')} {index + 1}
                        </Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                            <TextField label={l('Question', 'Intrebare')} value={item.question} fieldId={`faq.${index}.question`} onFocus={focusPreview(`faq.${index}.question`)} onChange={(value) => updateObjectArrayValue('faq', 'items', index, 'question', value)} />
                            <TextAreaField label={l('Answer', 'Raspuns')} value={item.answer} fieldId={`faq.${index}.answer`} onFocus={focusPreview(`faq.${index}.answer`)} onChange={(value) => updateObjectArrayValue('faq', 'items', index, 'answer', value)} />
                        </SimpleGrid>
                    </Box>
                ))}
            </EditorSection>

            <EditorSection
                eyebrow={l('Footer', 'Footer')}
                title={l('Footer and Legal Area', 'Footer si zona legala')}
                description={l(
                    'This section is standard and is always included in the exported Elementor template. New landing pages reuse the terms & conditions link from your last saved landing page automatically.',
                    'Aceasta sectiune este standard si este inclusa mereu in template-ul Elementor exportat. Paginile noi preiau automat link-ul de termeni si conditii din ultima pagina de landing salvata.'
                )}
            >
                <Box mb="4">
                    <TextField
                        label={l('Terms & conditions link', 'Link termeni si conditii')}
                        value={content.footer.legal_link_url}
                        placeholder="https://site-ul-tau.ro/termeni-si-conditii"
                        fieldId="footer.legalText"
                        onFocus={focusPreview('footer.legalText')}
                        onChange={(value) => updateContent('footer', 'legal_link_url', value)}
                    />
                    <Text fontSize="xs" color="gray.400" mt="2">
                        {l(
                            'The legal button in the footer will open this link. The button text stays standard.',
                            'Butonul legal din footer va deschide acest link. Textul butonului ramane standard.'
                        )}
                    </Text>
                </Box>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                    <Box p="4" borderWidth="1px" borderColor={infoCardBorderColor} borderRadius="xl" bg={infoCardBg}>
                        <Text fontWeight="700" color={infoCardTitleColor} mb="1">{l('SAL image', 'Imagine SAL')}</Text>
                        <Text fontSize="xs" color="gray.500">{l('Image URL kept in code, hidden in UI', 'URL imagine pastrat in cod, ascuns in UI')}</Text>
                    </Box>
                    <Box p="4" borderWidth="1px" borderColor={infoCardBorderColor} borderRadius="xl" bg={infoCardBg}>
                        <Text fontWeight="700" color={infoCardTitleColor} mb="1">{l('SOL image', 'Imagine SOL')}</Text>
                        <Text fontSize="xs" color="gray.500">{l('Image URL kept in code, hidden in UI', 'URL imagine pastrat in cod, ascuns in UI')}</Text>
                    </Box>
                </SimpleGrid>
            </EditorSection>
                </Box>
                <Box
                    position={{ base: 'static', xl: 'fixed' }}
                    top={{ xl: `${previewTop}px` }}
                    right={{ xl: '40px' }}
                    bottom={{ xl: '24px' }}
                    width={{ base: 'auto', xl: '420px' }}
                    zIndex={{ xl: 5 }}
                >
                    <LiveLandingPreview content={content} supportsReviews={supportsReviews} activeField={activeField} isRo={isRo} onFieldSelect={focusEditorField} />
                </Box>
            </Grid>
        </Box>
    );
};

export default LandingPageBuilder;
